import React, { useEffect } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { openDecagonChat } from 'components/Decagon';
import Message from 'components/Message';
import When from 'components/When';
import { SERVER_DATE_FORMAT } from 'constants/logic';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';

import {
  trackNoSchedulesContactUsClicked,
  trackNoSchedulesDateViewed,
  trackNoSchedulesManageClicked,
} from './ScheduleListV2.analytics';
import {
  getScheduleListV2EmptyStateVariant,
  isScheduleDateInPast,
  SCHEDULE_LIST_V2_EMPTY_STATE_VARIANT,
} from './ScheduleListV2EmptyState.helpers';
import messages from './ScheduleListV2EmptyState.messages';
import ScheduleListV2IntegrationsUpsell from './ScheduleListV2IntegrationsUpsell';

import './ScheduleListV2EmptyState.scss';

const { element } = bem('schedule-list-v2-empty-state');

const variantContent = {
  [SCHEDULE_LIST_V2_EMPTY_STATE_VARIANT.MANUAL_TODAY_FUTURE]: {
    showIntegrationsUpsell: true,
    showManageSchedule: true,
    subtitle: messages.manualTodayFutureSubtitle,
    title: messages.manualTodayFutureTitle,
  },
  [SCHEDULE_LIST_V2_EMPTY_STATE_VARIANT.MANUAL_PAST]: {
    showIntegrationsUpsell: false,
    showManageSchedule: true,
    subtitle: messages.manualPastSubtitle,
    title: messages.manualPastTitle,
  },
  [SCHEDULE_LIST_V2_EMPTY_STATE_VARIANT.INTEGRATED_TODAY_FUTURE]: {
    showContactUs: true,
    showIntegrationsUpsell: false,
    showManageSchedule: false,
    subtitle: messages.integratedTodayFutureSubtitle,
    title: messages.integratedTodayFutureTitle,
  },
  [SCHEDULE_LIST_V2_EMPTY_STATE_VARIANT.INTEGRATED_PAST]: {
    showIntegrationsUpsell: false,
    showManageSchedule: false,
    subtitle: messages.integratedPastSubtitle,
    title: messages.integratedPastTitle,
  },
};

const ScheduleListV2EmptyState = ({ isManualStudio, startDate, venueId, venueTimezone }) => {
  const navigate = useNavigate();
  const isPastDate = isScheduleDateInPast({ startDate, venueTimezone });
  const variant = getScheduleListV2EmptyStateVariant({ isManualStudio, isPastDate });
  const { showContactUs, showIntegrationsUpsell, showManageSchedule, subtitle, title } = variantContent[variant];
  const selectedDayKey = startDate ? format(startDate, SERVER_DATE_FORMAT) : null;

  useEffect(() => {
    trackNoSchedulesDateViewed({ venueId });
  }, [selectedDayKey, venueId]);

  const handleManageScheduleClick = () => {
    trackNoSchedulesManageClicked({ venueId });
    navigate(makePath.schedules({ focus: 'list', startDate, venueId }));
  };

  const handleContactUsClick = () => {
    trackNoSchedulesContactUsClicked({ venueId });
    openDecagonChat();
  };

  return (
    <div className={element('container', { withUpsell: showIntegrationsUpsell })}>
      <div className={element('message')}>
        <Message as="p" className={element('title')} message={title} />
        <Message as="p" className={element('subtitle')} message={subtitle} />
        <When predicate={showManageSchedule}>
          <Message
            as={Button}
            className={element('cta')}
            message={messages.manageSchedule}
            onClick={handleManageScheduleClick}
            styled="primary"
          />
        </When>
        <When predicate={showContactUs}>
          <Message
            as={Button}
            className={element('cta')}
            message={messages.contactUs}
            onClick={handleContactUsClick}
            styled="primary"
          />
        </When>
      </div>
      <When predicate={showIntegrationsUpsell}>
        <ScheduleListV2IntegrationsUpsell venueId={venueId} />
      </When>
    </div>
  );
};

ScheduleListV2EmptyState.propTypes = {
  isManualStudio: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  venueId: PropTypes.number,
  venueTimezone: PropTypes.string,
};

export default ScheduleListV2EmptyState;
